import { DiscountExceedsCategoryError, confirmReceipt, linhaConfirmada } from '@domain/receipt'
import type { Receipt, ReceiptItem, ReviewTotals } from '@domain/receipt'
import type { Product } from '@domain/product'
import { formatBRL } from '@shared/money/format'

/**
 * O que confirmar uma nota vai gravar — dito ANTES, e calculado pela mesma
 * conta que grava. Decisão 305.
 *
 * ## Por que existe
 *
 * Confirmar é a escrita irreversível no histórico de preço. A crítica 302 a
 * achou num botão cru que gravava num toque e terminava com uma frase fixa —
 * "o detalhamento e os preços já estão gravados" —, falsa sem transação e
 * falsa sem data. E a linha "sugerido" que ninguém aceitou entrava sem
 * produto, sem que a tela dissesse.
 *
 * ## A mesma conta da Function
 *
 * A prévia chama `confirmReceipt` com as linhas traduzidas por
 * `linhaConfirmada` — as regras de `onReceiptConfirmed`. Uma contagem própria
 * aqui discordaria da Function no dia em que uma regra mudasse, e a tela
 * prometeria um preço que nunca foi gravado.
 *
 * **O teto passado é o maior possível**, e de propósito: a prévia pergunta O
 * QUE vai ser gravado, e não se cabe na transação — essa conta depende do
 * valor da transação, que a tela de nota não carrega. O abatimento que passa
 * da categoria, esse sim, ela pega, porque não depende de teto nenhum.
 */

export type ItemDoResumo = {
  texto: string
  tom: 'ok' | 'aviso'
  /** A linha que o item cita, quando cita uma — a folha leva até ela. */
  linhaId?: string
}

export type Previa = {
  itens: ItemDoResumo[]
  /** Quando há, confirmar não deve acontecer — a Function recusaria. */
  bloqueio: string | null
  /** Quantos pontos de preço a confirmação grava. */
  precos: number
}

type Entrada = {
  linhas: ReceiptItem[]
  produtoDe: (linha: ReceiptItem) => Product | null
  nota: Pick<Receipt, 'transactionId' | 'categoryId' | 'extracted'>
  totais: ReviewTotals
  semCategoriaCents: number
}

export function previaDaConfirmacao({
  linhas,
  produtoDe,
  nota,
  totais,
  semCategoriaCents,
}: Entrada): Previa {
  const data = nota.extracted?.issuedAt ?? null
  const usadas = linhas.filter((l) => l.matchStatus !== 'rejected')

  let precos = 0
  let aprendizados = 0
  let bloqueio: string | null = null
  try {
    const c = confirmReceipt(
      usadas.map((l) => linhaConfirmada(l, produtoDe(l))),
      Number.MAX_SAFE_INTEGER,
      data,
      {
        cnpj: nota.extracted?.merchantCnpj ?? null,
        name: nota.extracted?.merchantName ?? null,
      },
      nota.categoryId,
    )
    precos = c.pricePoints.length
    aprendizados = c.learnings.length
  } catch (erro: unknown) {
    if (!(erro instanceof DiscountExceedsCategoryError)) throw erro
    bloqueio =
      'Um abatimento passa do que a categoria dele tem nesta nota. Ajuste a linha antes de confirmar.'
  }

  const itens: ItemDoResumo[] = []

  // A conta, primeiro: é o fato que a pessoa veio conferir com o papel.
  if (totais.receiptCents !== null && totais.differenceCents === 0) {
    itens.push({ texto: `A conta fecha: ${formatBRL(totais.receiptCents)}.`, tom: 'ok' })
  } else if (totais.differenceCents !== null && totais.differenceCents !== 0) {
    itens.push({
      texto: `A soma dos itens difere do total da nota em ${formatBRL(Math.abs(totais.differenceCents))}.`,
      tom: 'aviso',
    })
  }

  if (data === null) {
    itens.push({
      texto: 'Nenhum preço vai para o histórico: a leitura não achou a data da nota.',
      tom: 'aviso',
    })
  } else if (precos > 0) {
    itens.push({
      texto: precos === 1 ? '1 preço vai para o histórico.' : `${String(precos)} preços vão para o histórico.`,
      tom: 'ok',
    })
  }

  const pendentes = usadas.filter((l) => l.matchStatus === 'pending' && l.productId !== null)
  const naoAceitas = pendentes.length
  if (naoAceitas > 0) {
    itens.push({
      // A primeira delas: "não foi aceita" sem dizer qual mandava procurar.
      linhaId: pendentes[0]?.id,
      texto:
        naoAceitas === 1
          ? '1 sugestão não foi aceita — essa linha entra sem produto, e o preço dela não é gravado.'
          : `${String(naoAceitas)} sugestões não foram aceitas — essas linhas entram sem produto, e o preço delas não é gravado.`,
      tom: 'aviso',
    })
  }

  if (semCategoriaCents > 0) {
    itens.push({ texto: `${formatBRL(semCategoriaCents)} ficam sem categoria.`, tom: 'aviso' })
  }

  if (nota.transactionId === null) {
    itens.push({
      texto: 'Sem transação vinculada: o detalhamento só é gravado quando você vincular uma.',
      tom: 'aviso',
    })
  }

  if (aprendizados > 0) {
    itens.push({
      texto:
        aprendizados === 1
          ? '1 correção de produto vai ensinar o próximo cupom.'
          : `${String(aprendizados)} correções de produto vão ensinar o próximo cupom.`,
      tom: 'ok',
    })
  }

  return { itens, bloqueio, precos }
}

/**
 * A frase depois de confirmar, escrita a partir do que aconteceu.
 *
 * A antiga era fixa — "O detalhamento e os preços já estão gravados." — e
 * mentia em três dos quatro casos possíveis.
 */
export function mensagemDeConfirmada(
  nota: Pick<Receipt, 'transactionId' | 'extracted'>,
  precos: number,
): string {
  const partes = [
    nota.transactionId === null
      ? 'o detalhamento sai quando você vincular uma transação'
      : 'o detalhamento está gravado',
  ]
  if (precos > 0) {
    partes.push(precos === 1 ? '1 preço foi para o histórico' : `${String(precos)} preços foram para o histórico`)
  } else if (nota.extracted?.issuedAt == null) {
    partes.push('nenhum preço foi para o histórico, porque a nota não tem data')
  }
  return `Nota confirmada: ${partes.join('; ')}.`
}
